import { useCallback, useEffect, useState } from 'react';

import { listContactRequests } from '../../api/contactRequests';
import type { ContactRequestCounts } from '../../api/contactRequests';
import { ApiError } from '../../api/errors';
import { listOpportunities } from '../../api/opportunities';
import type { SalesPipelineSummary } from '../../api/opportunities';
import { ProjectHealth, ProjectStatus, listMyProjectTasks, listProjects } from '../../api/projects';
import type {
  MyProjectTaskItem,
  ProjectHealthValue,
  ProjectListItem,
  ProjectStatusValue,
} from '../../api/projects';
import { PermissionCodes } from '../../auth/permissions';
import { useAuth } from '../../auth/useAuth';

/** Một nguồn số liệu: chưa tải, đã có, hoặc hỏng kèm lời giải thích. */
export type Loadable<T> =
  | { readonly state: 'loading' }
  | { readonly state: 'ready'; readonly data: T }
  | { readonly state: 'error'; readonly message: string };

/** Số liệu dự án đã gom sẵn cho bảng điều khiển. */
export interface ProjectStats {
  /** Số dự án đang mở theo từng trạng thái (không tính đã đóng, đã hủy). */
  readonly openByStatus: ReadonlyMap<ProjectStatusValue, number>;
  /** Số dự án đang mở theo từng mức sức khỏe. */
  readonly openByHealth: ReadonlyMap<ProjectHealthValue, number>;
  readonly openCount: number;
  readonly overdueCount: number;
  readonly closedCount: number;
  /** Có nhiều dự án hơn số đã tải — các con số chỉ tính trên phần đã tải. */
  readonly isTruncated: boolean;
}

/** Toàn bộ số liệu của trang chủ. Nguồn nào người dùng không có quyền thì là rỗng. */
export interface DashboardData {
  readonly projects: Loadable<ProjectStats> | null;
  readonly myTasks: Loadable<readonly MyProjectTaskItem[]> | null;
  readonly pipeline: Loadable<SalesPipelineSummary> | null;
  readonly contacts: Loadable<ContactRequestCounts> | null;
}

/** Số dự án tối đa tải một lần — trần phân trang của máy chủ. */
const projectPageSize = 200;

const openStatuses: readonly ProjectStatusValue[] = [
  ProjectStatus.Draft,
  ProjectStatus.InProgress,
  ProjectStatus.OnHold,
  ProjectStatus.Installing,
  ProjectStatus.PendingAcceptance,
  ProjectStatus.UnderWarranty,
];

const healthLevels: readonly ProjectHealthValue[] = [
  ProjectHealth.Good,
  ProjectHealth.NeedsAttention,
  ProjectHealth.AtRisk,
  ProjectHealth.Critical,
];

function summarizeProjects(items: readonly ProjectListItem[], totalCount: number): ProjectStats {
  const openByStatus = new Map<ProjectStatusValue, number>(openStatuses.map((status) => [status, 0]));
  const openByHealth = new Map<ProjectHealthValue, number>(healthLevels.map((level) => [level, 0]));
  let overdueCount = 0;
  let closedCount = 0;

  for (const project of items) {
    if (!openByStatus.has(project.status)) {
      closedCount += 1;
      continue;
    }

    openByStatus.set(project.status, (openByStatus.get(project.status) ?? 0) + 1);
    openByHealth.set(project.health, (openByHealth.get(project.health) ?? 0) + 1);

    if (project.isOverdue) {
      overdueCount += 1;
    }
  }

  return {
    openByStatus,
    openByHealth,
    openCount: items.length - closedCount,
    overdueCount,
    closedCount,
    isTruncated: totalCount > items.length,
  };
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/**
 * Nạp số liệu cho trang chủ.
 *
 * Mỗi nguồn nạp riêng và hỏng riêng: không đọc được liên hệ website thì các biểu đồ dự án vẫn hiện.
 * Nguồn nào người dùng không có quyền thì KHÔNG gọi máy chủ — thẻ tương ứng cũng không hiện.
 */
export function useDashboardData(): DashboardData & { readonly reload: () => void } {
  const { hasPermission } = useAuth();

  const canSeeProjects = hasPermission(PermissionCodes.ProjectView);
  const canSeeTasks = hasPermission(PermissionCodes.ProjectTaskView);
  const canSeePipeline = hasPermission(PermissionCodes.OpportunityView);
  const canSeeContacts = hasPermission(PermissionCodes.ContactRequestView);

  const [reloadToken, setReloadToken] = useState(0);
  const [data, setData] = useState<DashboardData>({
    projects: null,
    myTasks: null,
    pipeline: null,
    contacts: null,
  });

  useEffect(() => {
    let isCurrent = true;

    function settle<T>(
      key: keyof DashboardData,
      promise: Promise<T>,
      fallback: string,
    ): void {
      promise.then(
        (value) => {
          if (isCurrent) {
            setData((current) => ({ ...current, [key]: { state: 'ready', data: value } }));
          }
        },
        (error: unknown) => {
          if (isCurrent) {
            setData((current) => ({
              ...current,
              [key]: { state: 'error', message: errorMessage(error, fallback) },
            }));
          }
        },
      );
    }

    if (canSeeProjects) {
      settle(
        'projects',
        listProjects({ pageNumber: 1, pageSize: projectPageSize }).then((page) =>
          summarizeProjects(page.items, page.totalCount),
        ),
        'Không tải được số liệu dự án.',
      );
    }

    if (canSeeTasks) {
      settle('myTasks', listMyProjectTasks(false), 'Không tải được việc của bạn.');
    }

    if (canSeePipeline) {
      settle('pipeline', listOpportunities({}), 'Không tải được phễu cơ hội.');
    }

    if (canSeeContacts) {
      settle(
        'contacts',
        listContactRequests({ needsAction: true, pageSize: 1 }).then((page) => page.counts),
        'Không tải được liên hệ từ website.',
      );
    }

    return () => {
      isCurrent = false;
    };
  }, [canSeeProjects, canSeeTasks, canSeePipeline, canSeeContacts, reloadToken]);

  const reload = useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  /*
    Có quyền mà chưa có số liệu là đang tải; không có quyền là rỗng để thẻ không hiện. Khi tải lại,
    số cũ vẫn hiện cho tới khi có số mới — biểu đồ không nháy trắng.
  */
  const loading = { state: 'loading' } as const;

  return {
    projects: canSeeProjects ? (data.projects ?? loading) : null,
    myTasks: canSeeTasks ? (data.myTasks ?? loading) : null,
    pipeline: canSeePipeline ? (data.pipeline ?? loading) : null,
    contacts: canSeeContacts ? (data.contacts ?? loading) : null,
    reload,
  };
}

/**
 * Viết gọn một số tiền: 1,2 tỷ · 350 triệu · 85.000.
 *
 * Các tổng tiền của phễu cộng thẳng theo số, chưa quy đổi đồng tiền (hệ thống chưa có tỷ giá) —
 * nơi hiển thị phải nói rõ điều đó.
 */
export function formatCompactMoney(value: number): string {
  const format = (amount: number): string =>
    amount.toLocaleString('vi-VN', { maximumFractionDigits: 1 });

  if (Math.abs(value) >= 1_000_000_000) {
    return `${format(value / 1_000_000_000)} tỷ`;
  }

  if (Math.abs(value) >= 1_000_000) {
    return `${format(value / 1_000_000)} triệu`;
  }

  return value.toLocaleString('vi-VN');
}
